import { compact } from 'lodash';
import qs from 'qs';
import * as Api from './Api';
import { Page } from '../Utils/Types/UiSection';
import { ImportScope } from '../Utils/Types/Import';
import { Post } from '../Utils/Types/Posts';
import Note from '../Utils/Types/Note';
import { User } from '../Utils/Types/User';
import { Space } from '../Utils/Types/Space';

export type QueryConfig<T> = {
  queryKey: string[];
  queryFn: (...args: any[]) => Promise<T>;
};

export type MutationConfig<T> = {
  mutationKey?: any[];
  mutationFn: (...args: any[]) => Promise<T>;
};

interface QueriesConfig<T> {
  [key: string]: (...args: any[]) => QueryConfig<T> | MutationConfig<T>;
}

const queriesConfig = {
  //
  // Mutations
  //
  postDiscussionNote: (postId: Post['id'], { parentId, type = 'email' }: any) => {
    return {
      mutationFn: Api.postDiscussionNote(postId, { parentId, type }),
    };
  },
  postCommentForComment: (postId: Post['id'], commentId: Note['id']) => {
    return {
      mutationFn: Api.postCommentForComment(postId, commentId),
    };
  },
  postComment: (postId: Post['id']) => {
    return {
      mutationFn: Api.postComment(postId),
    };
  },
  clonePost: (postId: Post['id']) => {
    return {
      mutationFn: Api.clonePost(postId),
    };
  },
  createFormResponse: (postId: Post['id']) => {
    return {
      mutationFn: Api.createFormResponse(postId),
    };
  },
  validateFormResponse: (postId: Post['id']) => {
    return {
      mutationFn: Api.validateFormResponse(postId),
    };
  },
  updateFormResponse: (postId: Post['id'], formResponseId: number | string) => {
    return {
      mutationFn: Api.updateFormResponse(postId, formResponseId),
    };
  },
  createOrUpdateSecuredStrategySubmission: (postId: Post['id'], responseId: number | string) => {
    return {
      mutationFn: responseId
        ? Api.updateSecuredStrategySubmission(postId, responseId)
        : Api.createSecuredStrategySubmission(postId),
    };
  },

  deleteFormResponse: (postId: Post['id'], formResponseId: number | string) => {
    return {
      mutationFn: Api.deleteFormResponse(postId, formResponseId),
    };
  },
  updateMenuItems: () => {
    return {
      mutationFn: Api.updateMenuItems,
    };
  },
  createAttendance: (postId: Post['id'], attendanceStatus: string) => {
    return {
      mutationFn: Api.createAttendance(postId, attendanceStatus),
    };
  },
  updateAttendance: (attendanceId: number | string) => {
    return {
      mutationFn: Api.updateAttendance(attendanceId),
    };
  },
  createPost: () => {
    return {
      mutationFn: Api.createPost,
    };
  },
  updatePost: (postId: Post['id']) => {
    return {
      mutationFn: Api.updatePost(postId),
    };
  },
  updatePostDashboard: (postId: Post['id']) => {
    return {
      mutationFn: Api.updatePostDashboard(postId),
    };
  },
  pinUnpin: (postId: Post['id']) => {
    return {
      mutationFn: Api.pinUnpinPost(postId),
    };
  },
  deletePost: (postId: Post['id']) => {
    return {
      mutationFn: Api.deletePost(postId),
    };
  },
  deleteManyPosts: (postIds: Post['id'][]) => {
    return {
      mutationFn: Api.deleteManyPosts(postIds),
    };
  },
  updateQuestion: (postId: Post['id']) => {
    return {
      mutationFn: Api.updateQuestion(postId),
    };
  },
  updateUiSections: () => {
    return {
      mutationFn: Api.updateUiSections,
    };
  },
  deleteVoter: (postId: Post['id'], voterId: any) => {
    return {
      mutationFn: Api.deleteVoter(postId, voterId),
    };
  },
  deleteManyVoters: (postId: Post['id'], voterIds: number[]) => {
    return {
      mutationFn: Api.deleteManyVoters(postId, voterIds),
    };
  },
  createImport: (postId: Post['id'], scope: ImportScope) => {
    return {
      mutationFn: Api.createImport(postId, scope),
    };
  },
  updateVotersDuplicate: (
    postId: Post['id'],
    voterDuplicateId: number,
    decision: 'keep' | null,
  ) => {
    return {
      mutationFn: Api.updateVotersDuplicate(postId, voterDuplicateId, decision),
    };
  },
  verifyPin: () => {
    return {
      mutationFn: Api.verifyPin(),
    };
  },
  translate: () => {
    return {
      mutationFn: Api.translate,
    };
  },
  createPassword: () => {
    return {
      mutationFn: Api.createPassword,
    };
  },
  resetPassword: (spaceId: Space['id']) => {
    return {
      mutationFn: Api.resetPassword(spaceId),
    };
  },
  updatePassword: (userId: User['id'], tokenId: string) => {
    return {
      mutationFn: Api.updatePassword(userId, tokenId),
    };
  },
  updateMyPassword: () => {
    return {
      mutationFn: Api.updateMyPassword,
    };
  },
  updateProfile: (spaceId: Space['id']) => {
    return {
      mutationFn: Api.updateProfile(spaceId),
    };
  },
  updateSpace: () => {
    return {
      mutationFn: Api.updateSpace(),
    };
  },
  //
  // Queries
  //
  getProfile: () => {
    return {
      queryKey: ['profile'],
      queryFn: Api.getProfile,
    };
  },
  getMySpaces: () => {
    return {
      queryKey: ['my-spaces'],
      queryFn: Api.getMySpaces,
    };
  },
  getCommentTranslation: (commentId: Note['id'], targetLang: string) => {
    return {
      queryKey: ['comment-translation', commentId?.toString(), targetLang],
      queryFn: Api.getCommentTranslation(commentId, targetLang),
    };
  },
  getAddressCities: (postId: Post['id']) => {
    return {
      queryKey: ['address-cities', postId?.toString()],
      queryFn: Api.getAddressCities(postId),
    };
  },
  getAttendances: (postId: Post['id']) => {
    return {
      queryKey: compact(['attendances', postId.toString()]),
      queryFn: Api.getAttendances(postId),
    };
  },
  getComments: (postId: Post['id'], queryParams: object) => {
    return {
      queryKey: compact(['comments', postId, qs.stringify(queryParams)]),
      queryFn: Api.getCommentsForPost(postId, queryParams),
    };
  },
  getDiscussionComments: (postId: Post['id'], queryParams: object) => {
    return {
      queryKey: compact(['comments', postId, qs.stringify(queryParams)]),
      queryFn: Api.getDiscussionNotes(postId, queryParams),
    };
  },
  getPostStatus: (postId: Post['id']) => {
    return {
      queryKey: ['post-status', postId?.toString()],
      queryFn: Api.getPostStatus(postId),
    };
  },
  getPost: (postId: Post['id']) => {
    return {
      queryKey: ['post', postId?.toString()],
      queryFn: Api.getPost(postId),
    };
  },
  getFormResponse: (postId: Post['id'], formResponseId: number | '') => {
    return {
      queryKey: ['form-response', postId?.toString(), formResponseId?.toString()],
      queryFn: Api.getFormResponse(postId, formResponseId),
    };
  },
  getFormResponses: (postId: Post['id'], queryParams: any = {}) => {
    return {
      queryKey: compact(['form-responses', postId, qs.stringify(queryParams)]),
      queryFn: Api.getFormResponses(postId, queryParams),
    };
  },
  getReportReasons: () => {
    return {
      queryKey: ['report-reasons'],
      queryFn: Api.getReportReasons(),
    };
  },
  getSecuredStrategySubmission: (postId: Post['id'], responseId: number | string) => {
    return {
      queryKey: ['secured-strategy-response', postId?.toString(), responseId?.toString()],
      queryFn: Api.getSecuredStrategySubmission(postId, responseId),
    };
  },
  getQuestionResults: (questionId: string) => {
    return {
      queryKey: ['question-results', questionId],
      queryFn: Api.getQuestionResults(questionId),
    };
  },
  getUiSections: (page: Page, queryParams: any = {}) => {
    return {
      queryKey: compact(['ui-sections', page, queryParams]),
      queryFn: Api.getUiSections(page, queryParams),
    };
  },
  getVoters: (postId: Post['id'], queryParams: any = {}) => {
    return {
      queryKey: compact(['voters', postId, qs.stringify(queryParams)]),
      queryFn: Api.getVoters(postId, queryParams),
    };
  },
  getImport: (postId: Post['id'], scope: ImportScope) => {
    return {
      queryKey: compact(['voters-import', postId?.toString(), scope]),
      queryFn: Api.getImport(postId, scope),
    };
  },
  getVotersDuplicates: (postId: Post['id'], refresh: boolean = false) => {
    return {
      queryKey: compact(['voters-duplicates', refresh.toString(), postId?.toString()]),
      queryFn: Api.getVotersDuplicates(postId, refresh),
    };
  },
} satisfies QueriesConfig<any>;

export default queriesConfig;
